import { Dispatch } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import createAsyncAction from '@Actions/create-async-action';
import { ActionConsts } from '@Definitions/index';
import OnlineBanner from '@Images/product-banners/appointment-online-banner.png';
import { ILocationTimings, ILocationTimingsDayWise } from '@Reducers/location';
import { ScheduleService } from '@Services/index';
import { LocationService } from '@Services/location';
import { RedcliffeService } from '@Services/Redcliffe';
import { GetLocationProvidersMap, GetLocationTimingsPayload } from '@Services/Schedule/schedule-payload';
import { DEFAULT_LOCATION_CODE, ONLINE_LOCATION_ID } from '@Utils/constants';
import { arrangeOfflineClinicsByCity, getClinicCodeToCityMap } from '@Utils/Helpers/location/helper';
import { LOG_ERROR } from '@Utils/logger';

export const LocationActions = {
  // eslint-disable-next-line unicorn/consistent-function-scoping
  GetClinicLocations: () => async (dispatch, getState) => {
    try {
      dispatch({ type: ActionConsts.Location.GetClinicLocationsInit });
      const result = await ScheduleService.GetClinicLocations();
      if (result?.status === 200) {
        const offlineClinicLocations = arrangeOfflineClinicsByCity(result?.data);
        const onlineLocation = result?.data?.find((location) => location.code === DEFAULT_LOCATION_CODE);

        dispatch({
          offlineClinicLocations: offlineClinicLocations,
          clinicLocationToCity: getClinicCodeToCityMap(result?.data),
          onlineLocation: {
            id: onlineLocation?.id,
            code: onlineLocation?.code,
            images: [OnlineBanner.src],
          },
          type: ActionConsts.Location.GetClinicLocationsSuccess,
        });
      } else {
        dispatch({
          message: result?.data?.message,
          type: ActionConsts.Location.GetClinicLocationsFail,
        });
      }
    } catch (error) {
      LOG_ERROR('Error: ScheduleActions>>GetClinicLocations', { error });
      dispatch({ type: ActionConsts.Location.GetClinicLocationsFail });
    }
  },
  GetClinicLocationsFuture: createAsyncAction({
    initAction: ActionConsts.Location.GetClinicLocationsInit,
    successAction: ActionConsts.Location.GetClinicLocationsSuccess,
    failAction: ActionConsts.Location.GetClinicLocationsFail,
    returnResult: true,

    asyncFn: async () => {
      const response = await ScheduleService.GetClinicLocations();
      if (response?.status === 200) {
        const offlineClinicLocations = arrangeOfflineClinicsByCity(response?.data);
        const onlineLocation = response?.data?.find((location) => location.code === DEFAULT_LOCATION_CODE);

        return {
          type: 'success',
          result: {
            offlineClinicLocations: offlineClinicLocations,
            clinicLocationToCity: getClinicCodeToCityMap(response?.data),
            onlineLocation: {
              id: onlineLocation?.id,
              code: onlineLocation?.code,
              images: [OnlineBanner.src],
            },
          },
        };
      } else return { type: 'fail', result: undefined };
    },
  }),
  GetClinicLocation: (locationId: string) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Location.GetClinicLocationInit });
      const result = await ScheduleService.GetClinicLocation(locationId);
      if (result?.status === 200) {
        dispatch({
          offlineClinicLocation: result?.data,
          type: ActionConsts.Location.GetClinicLocationSuccess,
        });
      } else {
        dispatch({
          type: ActionConsts.Location.GetClinicLocationFail,
        });
      }
    } catch (error) {
      LOG_ERROR('Error: ScheduleActions>>GetClinicLocation', error);
      dispatch({
        type: ActionConsts.Location.GetClinicLocationFail,
      });
    }
  },
  GetProviderLocations: (requestPayload: GetLocationProvidersMap) => async (dispatch: Dispatch) => {
    try {
      dispatch({ type: ActionConsts.Location.GetProviderLocationsInit });
      const result = await LocationService.GetProviderLocations(requestPayload);
      if (result?.status === 200) {
        dispatch({
          locationsWithProviders: result?.data,
          type: ActionConsts.Location.GetProviderLocationsSuccess,
        });
      } else {
        dispatch({
          type: ActionConsts.Location.GetProviderLocationsFail,
        });
      }
    } catch (error) {
      LOG_ERROR('Error: LocationActions>>GetProviderLocations', error);
      dispatch({
        type: ActionConsts.Location.GetProviderLocationsFail,
      });
    }
  },
  // eslint-disable-next-line unicorn/consistent-function-scoping
  ResetClinicLocationsData: () => async (dispatch: Dispatch) => {
    dispatch({
      type: ActionConsts.Location.ResetClinicLocationsData,
    });
  },
  GetRedcliffeLocalities: createAsyncAction({
    initAction: ActionConsts.Location.GetRedcliffeLocalitiesInit,
    successAction: ActionConsts.Location.GetRedcliffeLocalitiesSuccess,
    failAction: ActionConsts.Location.GetRedcliffeLocalitiesFail,

    asyncFn: async (requestPayload: { query: string }) => {
      const response = await RedcliffeService.GetRedcliffeLocalities(requestPayload);
      if (response.status === 200) {
        return { result: { localities: response.data }, type: 'success' };
      }
      return { result: { localities: [] }, type: 'fail' };
    },
  }),
  GetLocationTimings: (requestPayload: GetLocationTimingsPayload) => async (dispatch: Dispatch) => {
    try {
      const locationIds = [
        ...new Set(requestPayload.locationIds.filter((locationId) => locationId !== ONLINE_LOCATION_ID)),
      ];
      if (!locationIds?.length) return;
      dispatch({ type: ActionConsts.Location.GetLocationTimingsInit });
      const result = await LocationService.GetLocationTimings({
        ...requestPayload,
        locationIds,
      });
      if (result?.status === 200) {
        const locationTimings: Record<string, ILocationTimings[]> = {};

        if (requestPayload.locationIds && requestPayload.locationIds.length > 0) {
          for (const locationId of requestPayload.locationIds) {
            locationTimings[locationId] = [];
          }
        }
        for (const key in result?.data) {
          const uniqueTimings = new Map();
          for (const timing of result?.data[key]) {
            const adjustedStartTime = dayjs(timing.startTime).isBefore(dayjs(requestPayload.startTime))
              ? requestPayload.startTime
              : timing.startTime;
            const adjustedEndTime = dayjs(timing.endTime).isAfter(dayjs(requestPayload.endTime))
              ? requestPayload.endTime
              : timing.endTime;
            const timeKey = `${adjustedStartTime}_${adjustedEndTime}`;
            uniqueTimings.set(timeKey, {
              startTime: adjustedStartTime,
              endTime: adjustedEndTime,
            });
          }
          locationTimings[key] = [...uniqueTimings.values()];
        }
        const locationTimingsDayWise: ILocationTimingsDayWise = {};
        for (const locationId of Object.keys(locationTimings)) {
          const splitTimings = splitLocationTimings(locationTimings[locationId]);
          locationTimingsDayWise[locationId] = splitTimings;
        }
        dispatch({
          locationTimings: locationTimingsDayWise,
          type: ActionConsts.Location.GetLocationTimingsSuccess,
        });
      } else {
        dispatch({
          type: ActionConsts.Location.GetLocationTimingsFail,
        });
      }
    } catch (error) {
      LOG_ERROR('Error: LocationActions>>GetLocationTimings', error);
      dispatch({
        type: ActionConsts.Location.GetLocationTimingsFail,
      });
    }
  },
};

export const splitLocationTimings = (locationTimings: ILocationTimings[]) => {
  if (!locationTimings) return {};
  const next3Days = Array.from({ length: 3 }, (_, index) => dayjs().add(index, 'day').format('YYYY-MM-DD'));
  const timings: Record<string, ILocationTimings[]> = {}; // { '2025-04-24': [...], '2025-04-25': [...], '2025-04-26': [...], }
  for (const day of next3Days) {
    //get all timings for that fall between start and end of the day
    const filteredTimings = locationTimings.filter(
      (timing) =>
        dayjs(timing.startTime).isSame(day, 'day') ||
        dayjs(timing.endTime).isSame(day, 'day') ||
        (dayjs(timing.startTime).isBefore(dayjs(day).endOf('day')) &&
          dayjs(timing.endTime).isAfter(dayjs(day).startOf('day'))),
    );
    //if endTime is flowing over to next day, then cut it.
    const cutOffTimings = filteredTimings.map((timing) => {
      if (dayjs(timing.endTime).isAfter(dayjs(day).endOf('day'))) {
        return { ...timing, endTime: dayjs(day).endOf('day').toISOString() };
      }
      return timing;
    });

    timings[day] = cutOffTimings;
  }
  return timings;
};
